export interface VmTreatmentSelection {
  selectedValue?: unknown;
  treat1?: unknown;
  treat2?: unknown;
}

export interface VmCalculationInput {
  VM?: unknown;
  name?: unknown;
  SSI30?: unknown;
  SSI90?: unknown;
  KV100?: unknown;
}

export interface ShearedPredictionRow {
  Property?: unknown;
  Prediction?: unknown;
  pred_num?: unknown;
  [key: string]: unknown;
}

export type ShearedCalculationWarning = 'invalid-treatment' | 'aggregate-treatment' | 'invalid-kv100-domain';

export interface ShearedCalculationResult {
  predictions: ShearedPredictionRow[];
  warning?: ShearedCalculationWarning;
}

interface ActiveVm {
  treatmentFraction: number;
  modelInput: VmCalculationInput;
}

function parseFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return null;
  }

  const parsedValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function normalizeName(value: unknown): string {
  const name = typeof value === 'string' ? value.trim() : '';
  return name.toLowerCase() === 'nan' ? '' : name;
}

function getPredictionValue(
  predictions: ShearedPredictionRow[],
  propertyName: string,
  blendNumber: number
): number | null {
  const row = predictions.find(prediction => {
    const property = typeof prediction.Property === 'string' ? prediction.Property.trim() : '';
    const matchesBlend = prediction.pred_num === undefined || Number(prediction.pred_num) === blendNumber;
    return property === propertyName && matchesBlend;
  });

  return row ? parseFiniteNumber(row.Prediction) : null;
}

function nestedLog(value: number): number | null {
  const innerValue = 5.44 * value;
  if (!Number.isFinite(innerValue) || innerValue <= 1) {
    return null;
  }

  const result = Math.log(Math.log(innerValue));
  return result;
}

function getCycleProperty(cycleCount: 30 | 90): string {
  return `KV100 (${cycleCount} Cycles Sheared)`;
}

function isCycleProperty(property: unknown, cycleCount: 30 | 90): boolean {
  if (typeof property !== 'string') {
    return false;
  }

  const normalizedProperty = property.trim().toLowerCase();
  return normalizedProperty === getCycleProperty(cycleCount).toLowerCase()
    || normalizedProperty === `${cycleCount} cycles sheared viscosity`;
}

function markShearedResultsUnavailable(
  predictions: ShearedPredictionRow[],
  blendNumber: number
): ShearedPredictionRow[] {
  return predictions.map(prediction => {
    const matchesBlend = prediction.pred_num === undefined || Number(prediction.pred_num) === blendNumber;
    if (matchesBlend && (isCycleProperty(prediction.Property, 30) || isCycleProperty(prediction.Property, 90))) {
      return { ...prediction, Prediction: 'Not calculated' };
    }
    return prediction;
  });
}

function weightedSsi(activeVms: ActiveVm[], cycle: 30 | 90): number | null {
  const fieldName = cycle === 30 ? 'SSI30' : 'SSI90';
  let weightedTotal = 0;

  for (const activeVm of activeVms) {
    const ssi = parseFiniteNumber(activeVm.modelInput[fieldName]);
    if (ssi === null) {
      return null;
    }
    weightedTotal += activeVm.treatmentFraction * ssi;
  }

  const totalTreatment = activeVms.reduce((total, vm) => total + vm.treatmentFraction, 0);
  return weightedTotal / totalTreatment;
}

export function applyRevisedShearedKv100(
  predictions: ShearedPredictionRow[],
  blendNumber: number,
  vmSelections: VmTreatmentSelection[],
  modelInputs: VmCalculationInput[]
): ShearedCalculationResult {
  const treatField = blendNumber === 1 ? 'treat1' : 'treat2';
  const activeVms: ActiveVm[] = [];

  for (const selection of vmSelections) {
    const vmName = normalizeName(selection.selectedValue);
    if (!vmName) {
      continue;
    }

    const rawTreatment = selection[treatField];
    if (rawTreatment === null || rawTreatment === undefined || rawTreatment === '') {
      return { predictions };
    }
    if (typeof rawTreatment === 'string' && rawTreatment.trim().toLowerCase() === 'nan') {
      return { predictions };
    }

    const treatmentWtPercent = parseFiniteNumber(rawTreatment);
    if (treatmentWtPercent === null || treatmentWtPercent < 0) {
      return {
        predictions: markShearedResultsUnavailable(predictions, blendNumber),
        warning: 'invalid-treatment'
      };
    }
    if (treatmentWtPercent === 0) {
      continue;
    }

    const modelInput = modelInputs.find(input => normalizeName(input.VM ?? input.name) === vmName);
    if (!modelInput) {
      return { predictions };
    }

    activeVms.push({
      treatmentFraction: treatmentWtPercent / 100,
      modelInput
    });
  }

  if (activeVms.length === 0) {
    return { predictions };
  }

  const totalTreatment = activeVms.reduce((total, vm) => total + vm.treatmentFraction, 0);
  if (totalTreatment >= 1) {
    return {
      predictions: markShearedResultsUnavailable(predictions, blendNumber),
      warning: 'aggregate-treatment'
    };
  }

  const finishedOilKv100 = getPredictionValue(predictions, 'KV100', blendNumber);
  const vmKv100Values = activeVms.map(vm => parseFiniteNumber(vm.modelInput.KV100));
  if (finishedOilKv100 === null || vmKv100Values.some(value => value === null)) {
    return { predictions };
  }

  const finishedOilLog = nestedLog(finishedOilKv100);
  const vmLogs = vmKv100Values.map(kv100 => nestedLog(kv100 as number));

  if (finishedOilLog === null || vmLogs.some(value => value === null)) {
    return {
      predictions: markShearedResultsUnavailable(predictions, blendNumber),
      warning: 'invalid-kv100-domain'
    };
  }

  const weightedVmLog = activeVms.reduce((total, vm, index) => {
    return total + vm.treatmentFraction * (vmLogs[index] as number);
  }, 0);
  const baselineLog = (finishedOilLog - weightedVmLog) / (1 - totalTreatment);
  const baselineKv100 = Math.exp(Math.exp(baselineLog)) / 5.44;

  if (!Number.isFinite(baselineKv100)) {
    return {
      predictions: markShearedResultsUnavailable(predictions, blendNumber),
      warning: 'invalid-kv100-domain'
    };
  }

  const ssi30 = weightedSsi(activeVms, 30);
  const ssi90 = weightedSsi(activeVms, 90);
  const sheared30 = ssi30 === null
    ? null
    : finishedOilKv100 - (ssi30 / 100) * (finishedOilKv100 - baselineKv100);
  const sheared90 = ssi90 === null
    ? null
    : finishedOilKv100 - (ssi90 / 100) * (finishedOilKv100 - baselineKv100);

  if ((sheared30 !== null && !Number.isFinite(sheared30))
    || (sheared90 !== null && !Number.isFinite(sheared90))) {
    return {
      predictions: markShearedResultsUnavailable(predictions, blendNumber),
      warning: 'invalid-kv100-domain'
    };
  }

  const updatedPredictions = predictions.map(prediction => {
    const matchesBlend = prediction.pred_num === undefined || Number(prediction.pred_num) === blendNumber;
    if (matchesBlend && sheared30 !== null && isCycleProperty(prediction.Property, 30)) {
      return { ...prediction, Prediction: sheared30 };
    }
    if (matchesBlend && sheared90 !== null && isCycleProperty(prediction.Property, 90)) {
      return { ...prediction, Prediction: sheared90 };
    }
    return prediction;
  });

  return { predictions: updatedPredictions };
}